import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { CatalogForm } from '../../../components/CatalogForm';
import { Validators } from '../../../components/CatalogForm/validators';
import { UsuarioService } from '../../../services/UsuarioService';

const SITUACION_OPTIONS = [
  { value: 1, label: 'Activo' },
  { value: 10, label: 'Bloqueado' },
];

const UsuarioForm = (props) => {
  const routeParams = useParams();
  const id = props.id || props.id_user || props.match?.params?.id || routeParams?.id || null;

  const [rolesOptions, setRolesOptions] = useState([]);
  const [sucursalesOptions, setSucursalesOptions] = useState([]);
  const [clientesOptions, setClientesOptions] = useState([]);

  // Fetch initial form options (roles, sucursales, clientes)
  useEffect(() => {
    let isMounted = true;
    const fetchOptions = async () => {
      const data = await UsuarioService.obtenerOpcionesForm({ initial_data: true });
      if (isMounted && data) {
        if (data.roles) {
          setRolesOptions(data.roles.map((r) => ({ value: r.id, label: r.rol_nombre })));
        }
        if (data.sucursales) {
          setSucursalesOptions(data.sucursales.map((s) => ({ value: s.id, label: s.sucursal_nombre })));
        }
        if (data.clientes) {
          setClientesOptions(data.clientes.map((c) => ({ value: c.id, label: c.cliente_rsocial })));
        }
      }
    };
    fetchOptions();
    return () => {
      isMounted = false;
    };
  }, []);

  const handleSearchClientes = useCallback(async (query) => {
    if (!query || query.trim().length < 2) return [];
    const res = await UsuarioService.obtenerOpcionesForm({ cli_rs: query.trim() });
    if (res && res.clientes) {
      return res.clientes.map((c) => ({ value: c.id, label: c.cliente_rsocial }));
    }
    return [];
  }, []);

  const loadData = useCallback(async (recordId) => {
    const data = await UsuarioService.obtenerPorId(recordId);
    if (data) {
      // Don't pre-populate passwords
      delete data.password;
      delete data.confirm_password;
      data.cambiar_password = false;

      // If user has an assigned client, fetch client's label so autocomplete displays it
      if (data.id_cliente) {
        const clientRes = await UsuarioService.obtenerOpcionesForm({ initial_data: true, cli_id: data.id_cliente });
        if (clientRes && clientRes.clientes && clientRes.clientes.length > 0) {
          const c = clientRes.clientes[0];
          setClientesOptions((prev) => {
            const map = new Map();
            prev.forEach((p) => map.set(String(p.value), p));
            map.set(String(c.id), { value: c.id, label: c.cliente_rsocial });
            return Array.from(map.values());
          });
        }
      }
    }
    return data;
  }, []);

  const handleSubmit = useCallback(async (values) => {
    const payload = { ...values };
    // If editing and didn't opt to change password, omit password fields
    if (id && !payload.cambiar_password) {
      delete payload.password;
      delete payload.confirm_password;
    }
    delete payload.cambiar_password;

    return await UsuarioService.guardar(payload);
  }, [id]);

  const fields = useMemo(() => [
    {
      name: 'nombre',
      label: 'Nombre',
      type: 'text',
      col: 6,
      required: true,
      placeholder: 'Ej. Juan',
      validators: [Validators.required],
    },
    {
      name: 'apellido_paterno',
      label: 'Apellido Paterno',
      type: 'text',
      col: 6,
      required: true,
      placeholder: 'Ej. Pérez',
      validators: [Validators.required],
    },
    {
      name: 'apellido_materno',
      label: 'Apellido Materno',
      type: 'text',
      col: 6,
      required: true,
      placeholder: 'Ej. Gómez',
      validators: [Validators.required],
    },
    {
      name: 'email',
      label: 'Correo Electrónico',
      type: 'email',
      col: 6,
      required: true,
      placeholder: 'usuario@empresa.com',
      validators: [Validators.required, Validators.email],
    },
    // Switch to allow password update in edit mode (conditional render)
    {
      name: 'cambiar_password',
      label: 'Modificar contraseña de acceso',
      type: 'checkbox',
      col: 12,
      visible: (values, { isEdit }) => isEdit,
    },
    // Password (visible only in create mode or when cambiar_password is true)
    {
      name: 'password',
      label: 'Contraseña',
      type: 'password',
      col: 6,
      required: true,
      visible: (values, { isEdit }) => !isEdit || Boolean(values.cambiar_password),
      validators: [Validators.required, Validators.minLength(6)],
    },
    // Confirm Password (must match password)
    {
      name: 'confirm_password',
      label: 'Confirmar Contraseña',
      type: 'password',
      col: 6,
      required: true,
      visible: (values, { isEdit }) => !isEdit || Boolean(values.cambiar_password),
      validators: [
        Validators.required,
        Validators.custom((val, allValues) => {
          if (val && allValues.password && val !== allValues.password) {
            return 'Las contraseñas no coinciden';
          }
          return null;
        }),
      ],
    },
    {
      name: 'id_sucursal',
      label: 'Sucursal',
      type: 'select',
      col: 6,
      required: true,
      options: sucursalesOptions,
      validators: [Validators.required],
    },
    {
      name: 'id_rol',
      label: 'Rol',
      type: 'select',
      col: 6,
      required: true,
      options: rolesOptions,
      validators: [Validators.required],
    },
    {
      name: 'id_cliente',
      label: 'Cliente',
      type: 'autocomplete',
      col: 12,
      required: true,
      placeholder: 'Buscar por clave o razón social de cliente...',
      options: clientesOptions,
      onSearch: handleSearchClientes,
      validators: [Validators.required],
    },
    {
      name: 'todas_sucursales',
      label: 'Permitir crear guías de diferentes sucursales',
      type: 'checkbox',
      col: 6,
    },
    {
      name: 'login_web',
      label: 'Permitir entrar a la página',
      type: 'checkbox',
      col: 6,
    },
    {
      name: 'login_api',
      label: 'Permitir usar el web service (API)',
      type: 'checkbox',
      col: 6,
    },
    {
      name: 'situacion_estado',
      label: 'Situación',
      type: 'select',
      col: 6,
      required: true,
      options: SITUACION_OPTIONS,
      validators: [Validators.required],
    },
  ], [rolesOptions, sucursalesOptions, clientesOptions, handleSearchClientes]);

  return (
    <CatalogForm
      title="Usuario"
      subtitle="Administración de cuentas de acceso, roles y permisos del sistema"
      fields={fields}
      initialValues={{
        nombre: '',
        apellido_paterno: '',
        apellido_materno: '',
        email: '',
        password: '',
        confirm_password: '',
        cambiar_password: false,
        id_sucursal: '',
        id_rol: '',
        id_cliente: '',
        todas_sucursales: false,
        login_web: true,
        login_api: false,
        situacion_estado: 1,
      }}
      id={id}
      loadData={loadData}
      onSubmit={handleSubmit}
      backUrl="/Catalogo/Usuarios"
    />
  );
};

export default UsuarioForm;
